import { useMemo, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from "react";
import type { AccountInput } from "@/types";
import { shortDate } from "@/lib/date";
import { accountStore, assetStore, commodityStore, transactionStore } from "@/services/client";
import { Collapsible } from "@/features/shell/Collapsible";
import { EmptyState, SectionHead } from "@/features/shell/EmptyState";
import { BalanceSheetView } from "./BalanceSheet";
import { MonthStepper } from "./MonthStepper";
import {
  accountBalances,
  accountBalancesInBase,
  balanceSheet,
  balanceSheetTerms,
  buildAccountTree,
  formatMoney,
  formatMonth,
  inMonth,
  monthKey,
  naturalBalance,
  planned,
  posted,
  totalIncome,
  totalSpend,
  type AccountRecord,
  type AccountTreeNode,
  type AssetRecord,
} from "./ledger";

const MAX_TRANSACTIONS = 20;
const NO_MEMO = "Transaction";
const BASE_CURRENCY = "GBP";
const DEFAULT_PRECISION = 2;

/** The four classes shown nested under Owner's funds, in balance-sheet reading order. */
const NESTED_TYPES: AccountInput["accountType"][] = ["asset", "liability", "income", "expense"];

const EQUITY: AccountInput["accountType"] = "equity";

/** How each account class is named in the UI. The contract's `equity` is shown as
 * "Owner's funds" — the same thing, in the words the owner actually uses. */
const TYPE_LABEL: Record<AccountInput["accountType"], string> = {
  asset: "Assets",
  liability: "Liabilities",
  income: "Income",
  expense: "Expenses",
  equity: "Owner's funds",
};

/**
 * The money dashboard: net worth, this month's income and spend, the buckets money
 * is set aside in (accounts with a purpose), and the ledger behind it all. Every
 * total is drawn from `posted` transactions only — planned ones are the forecast,
 * never the actuals.
 */
export function MoneyPage() {
  const accounts = useSyncExternalStore(accountStore.subscribe, accountStore.getSnapshot);
  const transactions = useSyncExternalStore(
    transactionStore.subscribe,
    transactionStore.getSnapshot,
  );
  const commodities = useSyncExternalStore(commodityStore.subscribe, commodityStore.getSnapshot);
  const assets = useSyncExternalStore(assetStore.subscribe, assetStore.getSnapshot);

  // The period on screen. Everything but net worth is scoped to it.
  const currentMonth = monthKey(new Date().toISOString());
  const [month, setMonth] = useState(currentMonth);

  // A commodity's precision drives its formatting (2 for USD, 8 for BTC).
  const precisionOf = useMemo(() => {
    const bySymbol = new Map(commodities.map((c) => [c.fields.symbol, c.fields.precision]));
    return (symbol: string) => bySymbol.get(symbol) ?? DEFAULT_PRECISION;
  }, [commodities]);

  // Assets hang off the account whose balance they explain, never as a section of
  // their own — the ledger already counts their value once.
  const assetsByAccount = useMemo(() => {
    const byAccount = new Map<string, typeof assets>();
    for (const asset of assets) {
      const owned = byAccount.get(asset.fields.accountId) ?? [];
      owned.push(asset);
      byAccount.set(asset.fields.accountId, owned);
    }
    return byAccount;
  }, [assets]);

  const ledger = useMemo(() => {
    // Actuals only: a planned transaction never reaches a balance or a total.
    const actual = posted(transactions);
    const balances = accountBalances(actual);
    const period = inMonth(actual, month);
    const plannedPeriod = inMonth(planned(transactions), month);
    const sheet = balanceSheet(accounts, assets, accountBalancesInBase(actual));
    return {
      balances,
      period,
      plannedPeriod,
      sheet,
      terms: balanceSheetTerms(sheet),
      income: totalIncome(period, accounts),
      spend: totalSpend(period, accounts),
      // The forecast: what the plan for this month expects to come in.
      plannedIncome: totalIncome(plannedPeriod, accounts),
    };
  }, [transactions, accounts, assets, month]);

  if (accounts.length === 0) {
    return (
      <main className="console console-narrow">
        <SectionHead title="Money" />
        <EmptyState
          title="No accounts yet"
          hint="Money is double-entry: every transaction posts against accounts (asset, liability, equity, income, expense), and balances are derived from those postings."
        />
      </main>
    );
  }

  const base = (minorUnits: number) => formatMoney(minorUnits, BASE_CURRENCY, precisionOf(BASE_CURRENCY));
  // Everything an account-tree node needs to render: balances to roll up, the assets that
  // explain an account's value, and per-commodity precision for formatting.
  const treeCtx: AccountTreeContext = { balances: ledger.balances, assetsByAccount, precisionOf };
  // A bucket is just an account with a purpose — an activity or a long-term goal.
  const purposeAccounts = accounts.filter((a) => a.fields.activityId || a.fields.ltgId);
  const plannedInflow = (accountId: string) =>
    ledger.plannedPeriod.reduce(
      (total, t) =>
        total + t.fields.postings.filter((p) => p.accountId === accountId).reduce((s, p) => s + p.amount, 0),
      0,
    );

  return (
    <main className="console">
      <MonthStepper month={month} onChange={setMonth} currentMonth={currentMonth} />

      <ul className="stat-row">
        <Stat label="Net worth" value={base(ledger.terms.netWorth)} note={`all time · in ${BASE_CURRENCY}`} />
        <Stat
          label="Income"
          value={base(ledger.income)}
          note={ledger.plannedIncome !== 0 ? `planned ${base(ledger.plannedIncome)}` : "no plan"}
        />
        <Stat label="Spend" value={base(ledger.spend)} />
        <Stat label="Kept" value={base(ledger.income - ledger.spend)} note="income less spend" />
      </ul>

      <SectionHead title="Balance sheet" />
      <BalanceSheetView sheet={ledger.sheet} terms={ledger.terms} currency={BASE_CURRENCY} />

      <SectionHead title="Buckets" count={purposeAccounts.length} />
      {purposeAccounts.length === 0 ? (
        <EmptyState
          title="No buckets yet"
          hint="A bucket is money with a purpose — an account dedicated to an activity or a long-term goal. Set an activity or goal on an account to track money set aside for it."
        />
      ) : (
        <ul className="row-list">
          {purposeAccounts.map((account) => {
            const symbol = account.fields.commoditySymbol;
            const balance = naturalBalance(account.fields.accountType, ledger.balances.get(account.id) ?? 0);
            const inflow = plannedInflow(account.id);
            return (
              <li key={account.id} className="row">
                <span className="row-title">{account.fields.name}</span>
                <span className="row-meta">
                  {inflow !== 0
                    ? `${formatMoney(inflow, symbol, precisionOf(symbol))} planned this month`
                    : "not in this month's plan"}
                </span>
                <span className="row-amount">{formatMoney(balance, symbol, precisionOf(symbol))}</span>
              </li>
            );
          })}
        </ul>
      )}

      {/* The chart of accounts as a tree: Owner's funds is the root, the other four
       * classes nest under it (the accounting equation as a picture), and each class's
       * accounts nest by their real parentId. */}
      <SectionHead title="Accounts" count={accounts.length} />
      <AccountClass type={EQUITY} accounts={accounts} ctx={treeCtx} base={base}>
        {NESTED_TYPES.map((type) => (
          <AccountClass key={type} type={type} accounts={accounts} ctx={treeCtx} base={base} />
        ))}
      </AccountClass>

      {/* The ledger for the month on screen — actuals only, matching the stats. */}
      <SectionHead title="Transactions" count={ledger.period.length} />
      {ledger.period.length === 0 ? (
        <EmptyState
          title={`Nothing posted in ${formatMonth(month)}`}
          hint="Step to another month to see its ledger."
        />
      ) : (
        <ul className="row-list">
          {ledger.period.slice(0, MAX_TRANSACTIONS).map((transaction) => {
            const symbol = transaction.fields.postings[0]?.commodity ?? BASE_CURRENCY;
            return (
              <li key={transaction.id} className="row">
                <span className="row-title">{transaction.fields.memo ?? NO_MEMO}</span>
                <span className="row-meta">
                  {shortDate(transaction.fields.actualAt ?? transaction.fields.plannedAt ?? "")}
                </span>
                <span className="row-amount">
                  {formatMoney(magnitude(transaction.fields.postings), symbol, precisionOf(symbol))}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}

/** A transaction's size: its postings sum to zero, so the debits alone are its value. */
function magnitude(postings: { amount: number }[]): number {
  return postings.filter((p) => p.amount > 0).reduce((total, p) => total + p.amount, 0);
}

function Stat({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <li className="stat">
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
      {note && <span className="stat-note">{note}</span>}
    </li>
  );
}

/** Everything an account-tree node needs to render, bundled so it drills as one prop. */
type AccountTreeContext = {
  balances: Map<string, number>;
  assetsByAccount: Map<string, AssetRecord[]>;
  precisionOf: (symbol: string) => number;
};

/**
 * One account class as a collapsible fold: its accounts as a parentId tree, the class
 * subtotal kept visible when folded. `children` are the nested class folds shown under the
 * Owner's-funds root — a class with neither accounts nor children renders nothing.
 */
function AccountClass({
  type,
  accounts,
  ctx,
  base,
  children,
}: {
  type: AccountInput["accountType"];
  accounts: AccountRecord[];
  ctx: AccountTreeContext;
  base: (minorUnits: number) => string;
  children?: ReactNode;
}) {
  const group = accounts.filter((a) => a.fields.accountType === type);
  if (group.length === 0 && !children) return null;

  const subtotal = group.reduce(
    (total, account) => total + naturalBalance(type, ctx.balances.get(account.id) ?? 0),
    0,
  );
  const tree = buildAccountTree(group, ctx.balances);

  return (
    <Collapsible
      title={TYPE_LABEL[type]}
      count={children ? undefined : group.length}
      summary={base(subtotal)}
    >
      {tree.length > 0 && (
        <ul className="row-list">
          {tree.map((node) => (
            <AccountNode key={node.account.id} node={node} depth={0} ctx={ctx} />
          ))}
        </ul>
      )}
      {children}
    </Collapsible>
  );
}

/**
 * An account and its subtree as a flat run of depth-indented rows: the account's rolled-up
 * balance, then each child recursively, then the Asset records that explain this account's
 * own balance — nested so their value is counted once, as the contract intends.
 */
function AccountNode({
  node,
  depth,
  ctx,
}: {
  node: AccountTreeNode;
  depth: number;
  ctx: AccountTreeContext;
}) {
  const { account, children, total } = node;
  const symbol = account.fields.commoditySymbol;
  return (
    <>
      <li className="account tree-node" style={{ "--depth": depth } as CSSProperties}>
        <div className="row">
          <span className="row-title">{account.fields.name}</span>
          <span className="row-amount">{formatMoney(total, symbol, ctx.precisionOf(symbol))}</span>
        </div>
        {(ctx.assetsByAccount.get(account.id) ?? []).map((asset) => (
          <div key={asset.id} className="subrow">
            <span className="subrow-title">{asset.fields.description}</span>
            <span className="row-meta">
              {asset.fields.category} · {shortDate(asset.fields.acquiredAt)}
            </span>
            <span className="row-amount">
              {formatMoney(
                asset.fields.cost,
                asset.fields.commoditySymbol,
                ctx.precisionOf(asset.fields.commoditySymbol),
              )}
            </span>
          </div>
        ))}
      </li>
      {children.map((child) => (
        <AccountNode key={child.account.id} node={child} depth={depth + 1} ctx={ctx} />
      ))}
    </>
  );
}
